import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { StatusBadge } from "@/pages/Dashboard";

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).catch(() => {});
  }, []);

  const visible = cases.filter((c) => {
    const matchesText = !filter || (c.subject + " " + c.case_number + " " + c.requester_name).toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesText && matchesStatus;
  });

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-[1400px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Case Queue</div>
            <h1 className="font-serif text-4xl tracking-tight font-medium" data-testid="cases-title">All requests</h1>
          </div>
          <Link to="/cases/new" className="fa-btn-primary" data-testid="cases-new-button">
            <Plus className="w-4 h-4" /> New case
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
            <input
              data-testid="cases-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search case #, requester, or subject"
              className="fa-input pl-9"
            />
          </div>
          <select data-testid="cases-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="fa-input max-w-[200px]">
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="redaction">Redaction</option>
            <option value="legal_review">Legal review</option>
            <option value="approved">Approved</option>
            <option value="responded">Responded</option>
          </select>
        </div>

        <div className="fa-card">
          {visible.length === 0 ? (
            <div className="p-16 text-center text-slate-500">No cases match.</div>
          ) : (
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Case #</th>
                  <th>Requester</th>
                  <th>Subject</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Deadline</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <CaseRow key={c.case_id} c={c} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function CaseRow({ c }) {
  const daysLeft = Math.ceil((new Date(c.deadline) - new Date()) / 86400000);
  const overdue = daysLeft < 0;
  const atRisk = daysLeft >= 0 && daysLeft <= 3;
  return (
    <tr data-testid={`case-row-${c.case_id}`}>
      <td>
        <Link to={`/cases/${c.case_id}`} className="font-mono text-xs text-[#0B1B3D] hover:underline" data-testid={`case-link-${c.case_id}`}>
          {c.case_number}
        </Link>
      </td>
      <td>{c.requester_name}</td>
      <td className="max-w-md truncate">{c.subject}</td>
      <td><span className={`fa-badge fa-badge-${c.priority === "high" ? "danger" : c.priority === "low" ? "neutral" : "info"}`}>{c.priority}</span></td>
      <td><StatusBadge status={c.status} /></td>
      <td>
        {overdue ? (
          <span className="fa-badge fa-badge-danger">{Math.abs(daysLeft)}d overdue</span>
        ) : atRisk ? (
          <span className="fa-badge fa-badge-warning">{daysLeft}d left</span>
        ) : (
          <span className="text-slate-500 text-sm">{daysLeft}d</span>
        )}
      </td>
    </tr>
  );
}

export function NewCaseForm() {
  const nav = useNavigate();
  const [form, setForm] = useState({ requester_name: "", requester_email: "", subject: "", description: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/cases", form);
      nav(`/cases/${data.case_id}`);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-2xl">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">New case</div>
        <h1 className="font-serif text-4xl tracking-tight font-medium mb-8">Create a public records request</h1>

        <form onSubmit={submit} className="fa-card p-8 space-y-5">
          <div>
            <label className="fa-label">Requester name</label>
            <input data-testid="new-case-requester" required className="fa-input" value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} />
          </div>
          <div>
            <label className="fa-label">Requester email (optional)</label>
            <input type="email" data-testid="new-case-email" className="fa-input" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} />
          </div>
          <div>
            <label className="fa-label">Subject</label>
            <input data-testid="new-case-subject" required className="fa-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div>
            <label className="fa-label">Description</label>
            <textarea data-testid="new-case-description" required rows={5} className="fa-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="fa-label">Priority</label>
            <select data-testid="new-case-priority" className="fa-input max-w-xs" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          {err && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{String(err)}</div>}
          <div className="flex items-center gap-3 pt-3">
            <button data-testid="new-case-submit" type="submit" disabled={busy} className="fa-btn-primary">{busy ? "Creating…" : "Create case"}</button>
            <button type="button" onClick={() => nav("/cases")} className="fa-btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
